import { useCallback, useEffect, useState } from 'react';
import { GoogleAuthProvider, User, signInWithPopup } from 'firebase/auth';
import { auth } from '@/services/firebase';
import { isAdminEmail } from '@/config/admins';

/**
 * Google sign-in for the error feed, shared by the header and each error row.
 *
 * Firestore denies reads on `errors` to anyone who isn't an admin, so a
 * signed-out visitor gets an empty list — and the only sign-in control used to
 * live *inside* an error row, which therefore never rendered. The gate hid its
 * own key. This exists so the header can offer sign-in unconditionally.
 */
export function useAdminAuth() {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [authError, setAuthError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => auth.onAuthStateChanged(setUser), []);

  const signIn = useCallback(async () => {
    setAuthError(null);
    setBusy(true);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      if (!isAdminEmail(result.user.email)) {
        setAuthError('Not authorized');
        await auth.signOut();
        return false;
      }
      return true;
    } catch (err) {
      // A popup the user simply dismissed is not worth shouting about.
      const code = (err as { code?: string })?.code;
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        console.error('Sign-in failed:', err);
        setAuthError('Sign-in failed');
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthError(null);
    await auth.signOut();
  }, []);

  return {
    user,
    isAdmin: isAdminEmail(user?.email),
    authError,
    busy,
    signIn,
    signOut,
  };
}
